import React from "react";
import MapFull from "../common/components/MapFull";
import FloatingTopBar from "../components/areas/FloatingTopBar";
import UserPosition from "../common/components/UserPosition";
import { Box, Fab, Stack, useMediaQuery } from "@mui/material";
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Annotation } from "../common/classes/annotation";
import Annotate from "../common/dialogs/Annotate";
import AnnotationMarker from "../common/components/AnnotationMarker";
import { MapCameraChangedEvent, MapCameraProps } from "@vis.gl/react-google-maps";
import { useParams } from "react-router-dom";
import { gql, useMutation, useQuery } from "@apollo/client";
import { computeZoom } from "../common/utils/position";
import Loading from "../common/components/Loading";
import { and, whereNotDeleted } from "../common/utils/graphql";
import { IMAGE_CVI_TIFF, IMAGE_GNDVI_TIFF, IMAGE_GRVI_TIFF, IMAGE_KEY, IMAGE_LCI_TIFF, IMAGE_NDREI_TIFF, IMAGE_NDVI_TIFF, IMAGE_NDWI_TIFF, IMAGE_ORTHOPHOTO, IMAGE_OSAVI_TIFF, IMAGE_VARI_TIFF, IMAGE_DTM_TIFF, IMAGE_DSM_TIFF } from "../common/hooks/useCache";
import AnalysisLayer from "../common/components/AnalysisLayer";
import { db } from "../common/db/db";
import IndexSlider from "../common/components/IndexSlider";
import { getIndex, globalAvailableIndexes } from "../utils/indexes";
import Polyline from "../common/components/Polyline";
import { FieldDataItem } from "../common/classes/field_data_item";
import FloatingBottomBar from "../components/areas/FloatingBottomBar";

function Compare() {
  const [ latLngs, setLatLngs ] = React.useState<google.maps.LatLngLiteral[]>([]);
  const [ selectedRange, setSelectedRange ] = React.useState<[number, number] | undefined>(undefined);
  const [ min, setMin ] = React.useState<number | undefined>(-1);
  const [ max, setMax ] = React.useState<number | undefined>(1);
  const [ compareSelectedRange, setCompareSelectedRange ] = React.useState<[number, number] | undefined>(undefined);
  const [ compareMin, setCompareMin ] = React.useState<number | undefined>(-1);
  const [ compareMax, setCompareMax ] = React.useState<number | undefined>(1);
  const [ cameraState, setCameraState ] = React.useState<MapCameraProps | undefined>();
  const [ annotateDialogOpen, setAnnotateDialogOpen ] = React.useState<boolean>(false);
  const [ clickLatLng, setClickLatLng ] = React.useState<google.maps.LatLngLiteral | null>(null);
  const [ showButtons, setShowButtons ] = React.useState<boolean>(true);
  const [ locked, setLocked ] = React.useState<boolean>(true);
  const [ annotations, setAnnotations ] = React.useState<Annotation[]>([]);
  const [ availableIndexes, setAvailableIndexes ] = React.useState<string[]>([]);
  const [ selectedIndex, setSelectedIndex ] = React.useState<string | undefined>();
  const [ compareAnalysis, setCompareAnalysis ] = React.useState<{
    uuid: string,
    created_at: string,
    agriculture_analysis_data: {
      id: number,
      phase: string,
    }[],
  } | null>(null);
  const [ currentAnalysis, setCurrentAnalysis ] = React.useState<{
    uuid: string,
    created_at: string,
    agriculture_analysis_data: {
      id: number,
      phase: string,
    }[],
  } | null>(null);
  const [ analysis, setAnalysis ] = React.useState<{
    id: number,
    uuid: string,
    created_at: string,
    agriculture_analysis_data: {
      id: number,
      orthophoto_png: string,
      phase: string,
      index_data: {
        name: string,
        png: string,
        tiff: string,
        average: number,
        min: number,
        max: number,
        std_dev: number,
      }[],
    }[],
  }[]>([]);
  const [ field, setField ] = React.useState<{
    id: number,
    uuid: string,
    name: string,
  } | null>(null);
  const [ defaultCenter, setDefaultCenter ] = React.useState<google.maps.LatLngLiteral | undefined>();
  const [ defaultZoom, setDefaultZoom ] = React.useState<number | undefined>();
  const [ fieldDataItems, setFieldDataItems ] = React.useState<FieldDataItem[]>([]);
  const [ currentAnalysisData, setCurrentAnalysisData ] = React.useState<{
    id: number,
    phase: string,
  } | null>(null);

  const url = new URL(window.location.href);
  const analysisUUID = url.searchParams.get('analysis');
  const indexName = url.searchParams.get('index');

  const vertical = useMediaQuery('(max-width: 1000px)');

  if (indexName && !selectedIndex) {
    setSelectedIndex(indexName);
  }

  const { 
    fieldId: fieldUUID, 
    compareAnalysisID: compareAnalysisUUID, 
    compareIndex } = useParams();

  const { data } = useQuery(gql(`
    query FieldDetails($fieldUUID: uuid!) {
      fields(where: {uuid: {_eq: $fieldUUID}}) {
        id
        uuid
        name
        field_data_items(order_by: {created_at: desc}) {
          id
          created_at
          type
          value
        }
        analyses(where: `+and(whereNotDeleted() + '{type: {_eq: "agriculture"}}')+`, order_by: {created_at: desc}) {
          uuid
          created_at
          agriculture_analysis_data {
            id
            orthophoto
            orthophoto_png
            phase
            index_data(order_by: {name: asc}) {
              name
              png
              tiff
              average
              min
              max
              std_dev
            }
          }
        }
        lat_lngs(where: `+whereNotDeleted()+`, order_by: {id: asc}) {
          lat
          lng
        }
        annotations(where: `+whereNotDeleted()+`, order_by: {id: asc}) {
          uuid
          created_at
          annotation
          position(where: `+whereNotDeleted()+`, order_by: {id: asc}) {
            lat
            lng
          }
        }
      }
    }
  `), {
    variables: {
      fieldUUID,
    }
  });

  const [ deleteAnnotation ] = useMutation(gql(`
    mutation DeleteAnnotation($uuid: uuid!) {
      update_annotations(where: {uuid: {_eq: $uuid}}, _set: {deleted_at: "now()"}) {
        affected_rows
      }
    }
  `));

  const getImageFromIndex = (index?: string): IMAGE_KEY => {
    switch (index?.toLowerCase()) {
      case 'orthophoto':
        return IMAGE_ORTHOPHOTO;
      case 'vari':
        return IMAGE_VARI_TIFF;
      case 'grvi':
        return IMAGE_GRVI_TIFF;
      case 'ndvi':
        return IMAGE_NDVI_TIFF;
      case 'gndvi':
        return IMAGE_GNDVI_TIFF;
      case 'osavi':
        return IMAGE_OSAVI_TIFF;
      case 'cvi':
        return IMAGE_CVI_TIFF;
      case 'ndwi':
        return IMAGE_NDWI_TIFF;
      case 'ndrei':
        return IMAGE_NDREI_TIFF;
      case 'lci':
        return IMAGE_LCI_TIFF;
      case 'dtm':
        return IMAGE_DTM_TIFF;
      case 'dsm':
        return IMAGE_DSM_TIFF;
      default:
        return IMAGE_ORTHOPHOTO;
    }
  }

  React.useEffect(() => {
    setSelectedRange(undefined);
  }, [selectedIndex]);

  React.useEffect(() => {
    setCompareSelectedRange(undefined);
  }, [compareIndex]);

  React.useEffect(() => {
    if (data) {
      setField(data.fields[0]);
      setAnnotations(data.fields[0].annotations.filter((a: any) => a.position.length > 0).map((a: any) => {
        return {
          uuid: a.uuid,
          date: new Date(a.created_at),
          position: { lat: a.position[0].lat, lng: a.position[0].lng },
          annotation: a.annotation,
          audioBlobs: [],
          pictureBlobs: [],
          attachments: [],
        } as Annotation;
      }));
      setFieldDataItems(data.fields[0].field_data_items);
      setAnalysis(data.fields[0].analyses);
      let currAnalysis: any | null;
      if (!currentAnalysis) {
        currAnalysis = data.fields[0].analyses.find((a: any) => a.uuid === analysisUUID) ?? data.fields[0].analyses[0];
        setCurrentAnalysis(currAnalysis);
        if (currAnalysis) {
          const url = new URL(window.location.href);
          url.searchParams.set('analysis', currAnalysis.uuid);
          window.history.replaceState({}, document.title, url.toString());
        }
      } else {
        currAnalysis = currentAnalysis;
        setCurrentAnalysis(currAnalysis);
      }
      const compareAnalysis = data.fields[0].analyses.find((a: any) => a.uuid === compareAnalysisUUID);
      setCompareAnalysis(compareAnalysis ?? null);

      const b = new google.maps.LatLngBounds();
      const tmpLatLngs: google.maps.LatLngLiteral[] = [];
      data.fields[0].lat_lngs.forEach((latLng: any) => {
        b.extend(new google.maps.LatLng(latLng.lat, latLng.lng));
        tmpLatLngs.push({ lat: latLng.lat, lng: latLng.lng });
      });
      setDefaultCenter(b.getCenter().toJSON());
      setDefaultZoom(computeZoom(b, window.innerWidth, window.innerHeight));
      setLatLngs(tmpLatLngs);

      db.fields.put({
        id: data.fields[0].uuid,
        bounds: b.toJSON(),
      }).then(() => {});

      const aIdx = [];
      if (data.fields[0].analyses.length > 0) {
        const tmp = currAnalysis.agriculture_analysis_data;
        if (tmp && tmp.length > 0 && (tmp[0].orthophoto || tmp[0].orthophoto_png)) {
          aIdx.push('Orthophoto');
        }
        if (tmp && tmp.length > 0 && tmp[0].index_data) {
          for (const idx of tmp[0].index_data) {
            aIdx.push(idx.name.toUpperCase());
          }
        }
      }
      setAvailableIndexes(aIdx);
    } else {
      setLatLngs([]);
      setField(null);
      setAnalysis([]);
      setCurrentAnalysis(null);
      setCompareAnalysis(null);
      setAvailableIndexes([]);
      setDefaultCenter(undefined);
      setDefaultZoom(undefined);
    }
  }, [data, currentAnalysis, analysisUUID, compareAnalysisUUID]);

  React.useEffect(() => {
    setCurrentAnalysisData(currentAnalysis?.agriculture_analysis_data[0] ?? null);
  }, [currentAnalysis]);

  const handleCameraChange = React.useCallback((ev: MapCameraChangedEvent) => {
    setCameraState(ev.detail);
  }, []);

  const onBoundsChange = (bounds: google.maps.LatLngBounds) => {
    if (!currentAnalysis) {
      return;
    }

    db.analyses.put({
      id: currentAnalysis.uuid,
      bounds: bounds.toJSON(),
    }).then(() => {});
  }

  const onAnnotateDialogClose = (annotation: Annotation | undefined) => {
    if (annotation && clickLatLng) {
      annotation.uuid = annotations.length + '';
      annotation.position = clickLatLng;
      annotation.date = new Date();
      setAnnotations([...annotations, annotation]);
    }
    setAnnotateDialogOpen(false);
  }

  if (!defaultCenter || !defaultZoom) {
    return <Loading open />;
  }

  return (
    <Box sx={{
      width: '100%',
      height: '100%',
      display: vertical ? 'block' : 'flex',
      position: 'relative',
    }}>
      <Annotate open={annotateDialogOpen} onClose={onAnnotateDialogClose} latLng={clickLatLng} fieldID={field?.id ?? 0} />
      <div style={{
        width: vertical ? '100%' : '50%',
        height: vertical ? '50%' : '100%',
        borderBottom: vertical ? '2px solid white' : 'none',
        borderRight: vertical ? 'none' : '2px solid white',
      }}>
        <MapFull
        defaultCenter={defaultCenter}
        defaultZoom={defaultZoom}
        center={ locked && cameraState ? cameraState.center : undefined }
        zoom={ locked && cameraState ? cameraState.zoom : undefined }
        onClick={(e) => {
          setClickLatLng(e.detail.latLng);
          setAnnotateDialogOpen(true);
        }}>
          { /* <Polygon path={latLngs} zIndex={-100} /> */ }
          { globalAvailableIndexes.map((gIndex) => {
            if (!currentAnalysis || selectedIndex !== gIndex.id || !gIndex.imageKey) {
              return null;
            }
            if (gIndex.id === 'Orthophoto') {
              return (
                <AnalysisLayer key={gIndex.id} analysisID={currentAnalysis.uuid} image={IMAGE_ORTHOPHOTO}
                  height={ vertical ? '50%' : '100%' } width={ vertical ? '100%' : '50%'}
                  onBoundsChange={onBoundsChange} rgb />
              );
            }
            if (gIndex.id === 'None') {
              return null;
            }
            return (
              <AnalysisLayer
                key={gIndex.id}
                analysisID={currentAnalysis.uuid}
                image={gIndex.imageKey}
                height={ vertical ? '50%' : '100%' }
                width={ vertical ? '100%' : '50%'}
                onBoundsChange={onBoundsChange}
                range={selectedRange}
                setMin={(gIndex.id === 'DTM' || gIndex.id === 'DSM') ? setMin : undefined}
                setMax={(gIndex.id === 'DTM' || gIndex.id === 'DSM') ? setMax : undefined}
                clipMin={(gIndex.id === 'DTM' || gIndex.id === 'DSM') ? 0 : -1}
                clipMax={(gIndex.id === 'DTM' || gIndex.id === 'DSM') ? undefined : 1}
                rangeMin={(gIndex.id === 'DTM' || gIndex.id === 'DSM') ? undefined : 0}
                absolute={(gIndex.id === 'DTM' || gIndex.id === 'DSM') ? false : true}
                reversed={(gIndex.id === 'DTM' || gIndex.id === 'DSM')}
              />
            );
          })}
          <Polyline path={latLngs} closed />
          <UserPosition />
          { annotations.map((annotation) => (
            <AnnotationMarker key={annotation.uuid} annotation={annotation} onDelete={() => {
              deleteAnnotation({
                variables: {
                  uuid: annotation.uuid,
                }
              }).then(() => {
                setAnnotations(annotations.filter((a) => a.uuid !== annotation.uuid));
              }).catch((e) => {
                console.error("Failed to delete annotation", e);
              });
            }} />
          ))}
          { selectedIndex && selectedIndex !== 'Orthophoto' && selectedIndex !== 'GLB' && selectedIndex !== 'None' &&
            <IndexSlider
              splitColor
              min={min}
              max={max}
              onSelectedRange={(range) => {
                setSelectedRange(range);
              }}
              reversed
              height={showButtons ? (vertical ? 'calc(100% - 140px)' : 'calc(100% - 200px)') : 'calc(100% - 70px)'}
              top={showButtons ? '90px' : '30px'}
              side='left'
            />
          }
        </MapFull>
      </div>
      <div style={{
        width: vertical ? '100%' : '50%',
        height: vertical ? '50%' : '100%',
        borderTop: vertical ? '2px solid white' : 'none',
        borderLeft: vertical ? 'none' : '2px solid white',
      }}>
        <MapFull
        defaultCenter={defaultCenter}
        defaultZoom={defaultZoom}
        center={ locked && cameraState ? cameraState.center : undefined }
        zoom={ locked && cameraState ? cameraState.zoom : undefined }
        onCameraChanged={handleCameraChange}
        onClick={(e) => {
          setClickLatLng(e.detail.latLng);
          setAnnotateDialogOpen(true);
        }}>
          { /* <Polygon path={latLngs} zIndex={-100} /> */ }
          { compareAnalysisUUID && <AnalysisLayer
            analysisID={compareAnalysisUUID}
            image={getImageFromIndex(compareIndex)}
            height={ vertical ? '50%' : '100%' }
            width={ vertical ? '100%' : '50%'}
            top={ vertical ? '50%' : '0' }
            left={ vertical ? '0' : '50%' }
            rgb={compareIndex === 'Orthophoto'}
            onBoundsChange={onBoundsChange}
            setMin={(compareIndex === 'DTM' || compareIndex === 'DSM') ? setCompareMin : undefined}
            setMax={(compareIndex === 'DTM' || compareIndex === 'DSM') ? setCompareMax : undefined}
            range={compareIndex === 'Orthophoto' ? undefined : compareSelectedRange}
            clipMin={(compareIndex === 'DTM' || compareIndex === 'DSM') ? 0 : -1}
            clipMax={(compareIndex === 'DTM' || compareIndex === 'DSM') ? undefined : 1}
            rangeMin={(compareIndex === 'DTM' || compareIndex === 'DSM') ? undefined : 0}
            absolute={(compareIndex === 'DTM' || compareIndex === 'DSM' || compareIndex === 'Orthophoto') ? false : true}
            reversed={(compareIndex === 'DTM' || compareIndex === 'DSM')} /> }
          <Polyline path={latLngs} closed />
          <UserPosition />
          { annotations.map((annotation) => (
            <AnnotationMarker key={annotation.uuid} annotation={annotation} onDelete={() => {
              deleteAnnotation({
                variables: {
                  uuid: annotation.uuid,
                }
              }).then(() => {
                setAnnotations(annotations.filter((a) => a.uuid !== annotation.uuid));
              }).catch((e) => {
                console.error("Failed to delete annotation", e);
              });
            }} />
          ))}
          { compareIndex && compareIndex !== 'Orthophoto' && selectedIndex !== 'GLB' &&
            <IndexSlider
              splitColor
              min={compareMin}
              max={compareMax}
              onSelectedRange={(range) => {
                setCompareSelectedRange(range);
              }}
              reversed
              height={showButtons ? (vertical ? 'calc(100% - 140px)' : 'calc(100% - 200px)') : 'calc(100% - 70px)'}
              top={showButtons ? (vertical ? '30px' : '90px') : '30px'}
              side='left'
            />
          }
        </MapFull>
      </div>
      { showButtons && <FloatingTopBar
        availableIndexes={availableIndexes}
        selectedIndex={getIndex(selectedIndex)}
        onIndexChange={(index) => {
          setSelectedIndex(index.id);
          if (index) {
            const url = new URL(window.location.href);
            url.searchParams.set('index', index.id);
            window.history.replaceState({}, document.title, url.toString());
          }
        }}
        currentAnalysisUUID={currentAnalysis?.uuid}
        currentIndex={selectedIndex ?? undefined}
        field={field}
        fieldDataItems={fieldDataItems}
        agricultureAnalysisData={currentAnalysisData ?? undefined}
        fieldBoundary={latLngs}
        isCompare
        /> }
        { showButtons && <FloatingBottomBar
          analysis={analysis}
          currentAnalysis={currentAnalysis ?? undefined}
          selectedIndex={selectedIndex}
          setCurrentAnalysis={setCurrentAnalysis}
          zonationAvailable={
            !!selectedIndex &&
            selectedIndex !== 'Orthophoto' &&
            selectedIndex !== 'GLB' &&
            selectedIndex !== 'None'
          }
          hideButtons
        /> }
        <Stack
          direction='column'
          spacing={1}
          sx={{ 
            position: 'absolute',
            bottom: showButtons ? 90 : 25,
            right: 16,
            zIndex: 1000,
          }}
        >
          <Fab
            sx={{
              backgroundColor: 'white',
            }}
            onClick={() => setShowButtons(!showButtons)}
          >
            {showButtons ? <VisibilityIcon /> : <VisibilityOffIcon />}
          </Fab>
          <Fab
            sx={{
              backgroundColor: locked === true ? 'primary' : 'white',
            }}
            color={locked ? 'primary' : 'default'}
            onClick={() => setLocked(!locked)}
          >
            {locked ? <LockIcon /> : <LockOpenIcon />}
          </Fab>
        </Stack>
    </Box>
  );
}

export default React.memo(Compare);
